import { Button, Col, Row, Typography } from 'antd';
import { every, isEmpty, max, property, some } from 'lodash-es';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { GENERATE_REPORT_MUTATION } from '../app/graphql/reportQueries';
import { toServerDateOptional } from '../common/utils/dateUtils';
import { extractGraphqlEntity } from '../common/utils/graphqlUtils';
import { FAIcon } from '../components/adapters/fontAwesomeAdapters';
import { GraphQLMutatingButton } from '../components/data/asyncActionElements';
import { useFlashMessageContext } from '../components/dialogs/FlashMessageProvider';
import SimpleDialog from '../components/dialogs/SimpleDialog';
import { FormItemFieldRegistration } from '../components/forms/basicFormElements';
import { NoLabelForm, useFormContext } from '../components/forms/forms';
import { SlidingContent } from '../components/nav/SlidingContent';
import logoImg from '../resources/img/quick-online-logo-q.svg';
import { cssVariables } from '../styles/cssVariables';
import { useDialogToolbarStyles } from '../styles/dialogToolbar.styles';
import { pxToNumber } from '../utils/cssUtils';
import { ReportsAccountSelection } from '../widgets/reports/ReportsAccountSelection';
import { ReportsDateRange } from '../widgets/reports/ReportsDateRange';
import { ReportsDownloadSummary } from '../widgets/reports/ReportsDownloadSummary';
import { ReportsReportSelection } from '../widgets/reports/ReportsReportSelection';
import { useEmailProcessingDialog } from './useEmailProcessingDialog';
import { useReportsDialogStyles } from './ReportsDialog.styles';

const STEPS = {
  REPORT_TYPES: 0,
  ACCOUNT_SELECTION: 1,
  DATES: 2,
  REVIEW: 3,
};

const STEPS_DATA = [
  {
    name: 'reportSelection',
    component: ReportsReportSelection,
    canContinue: values => !isEmpty(values.selectedReports),
  },
  {
    name: 'accountSelection',
    component: ReportsAccountSelection,
    canContinue: values => !isEmpty(values.selectedAccounts),
  },
  {
    name: 'dateRange',
    component: ReportsDateRange,
    canContinue: values => {
      if (some(values.selectedReports, property('requiresDateRange'))) {
        return values.dateStart && values.dateEnd;
      }
      return true;
    },
  },
  {
    name: 'review',
    component: ReportsDownloadSummary,
  },
];

export function ReportsDialog({ open, close }) {
  const { openDialog, dialog } = useEmailProcessingDialog({
    documentType: 'Report',
    paragraph2Id: 'emailProcessingDialog.paragraph2.withLink',
  });
  const { styles } = useReportsDialogStyles();

  return (
    <>
      <SimpleDialog
        className={styles.reportsDialog}
        icon={<img src={logoImg} alt="" />}
        open={open}
        centered
        mask
        destroyOnHidden
        data-subject="reports"
        data-role="dialog"
      >
        <Typography.Title level={2}>
          <FormattedMessage id="reports.title" />
        </Typography.Title>
        <Typography.Title level={4}>
          <FormattedMessage id="reports.subtitle" />
        </Typography.Title>
        <NoLabelForm
          initialValues={{ selectedAccounts: [], selectedReports: [] }}
        >
          <FormItemFieldRegistration name="selectedAccounts" />
          <FormItemFieldRegistration name="selectedReports" />
          <FormItemFieldRegistration name="dateStart" />
          <FormItemFieldRegistration name="dateEnd" />
          <Steps
            close={close}
            onDone={() => {
              close();
              openDialog();
            }}
          />
        </NoLabelForm>
      </SimpleDialog>
      {dialog}
    </>
  );
}

function Steps({ close, onDone }) {
  const [step, setStep] = useState(STEPS.REPORT_TYPES);
  const stepData = STEPS_DATA[step];
  const { values, formInstance } = useFormContext();
  const { errorMessage } = useFlashMessageContext();
  const { styles } = useReportsDialogStyles();
  const { styles: toolbarStyles } = useDialogToolbarStyles();

  const saveNextDisabled = !stepData?.canContinue?.(values) ?? false;

  return (
    <>
      <div className="spaces-vert-md">
        <div className={styles.toolbar}>
          <div className={toolbarStyles.toolbarTitle}>
            <FormattedMessage id="reports.builder" />
          </div>
          <Row gutter={pxToNumber(cssVariables.spaceNorm)}>
            <Col>
              <Button size="large" onClick={close} className="width-100">
                <FormattedMessage id="buttons.cancel" />
              </Button>
            </Col>
            <Col>
              <GraphQLMutatingButton
                size="large"
                type="primary"
                className="width-190"
                disabled={step < STEPS.REVIEW}
                mutation={GENERATE_REPORT_MUTATION}
                mutationOptions={{
                  onCompleted: data => {
                    const { items } = extractGraphqlEntity(data);
                    if (!every(items, it => it.status === 'SUBMITTED')) {
                      errorMessage({ contentId: 'reports.generation.error' });
                    }
                    onDone();
                  },
                }}
                getMutationArgs={() => {
                  const vals = formInstance.getFieldsValue();
                  return {
                    variables: {
                      input: {
                        accountNumbers: vals.selectedAccounts.map(
                          property('number')
                        ),
                        reportNames: vals.selectedReports.map(property('code')),
                        startDate: toServerDateOptional(vals.dateStart),
                        endDate: toServerDateOptional(vals.dateEnd),
                      },
                    },
                  };
                }}
              >
                <span>
                  <FormattedMessage id="reports.download" />
                </span>
                <FAIcon icon="paper-plane" className="icon-16" />
              </GraphQLMutatingButton>
            </Col>
          </Row>
        </div>
        <Row gutter={pxToNumber(cssVariables.spaceNorm)} justify="end">
          <Col>
            <Button
              onClick={() => setStep(old => old - 1)}
              size="small"
              disabled={step === STEPS.REPORT_TYPES}
            >
              <FAIcon icon="chevron-left" />
              <span>
                <FormattedMessage id="buttons.back" />
              </span>
            </Button>
          </Col>
          {step < max(Object.values(STEPS)) && (
            <Col>
              <Button
                type="primary"
                size="small"
                onClick={() => setStep(old => old + 1)}
                disabled={saveNextDisabled}
              >
                <span>
                  <FormattedMessage id="buttons.saveNext" />
                </span>
                <FAIcon icon="chevron-right" />
              </Button>
            </Col>
          )}
        </Row>
      </div>
      <SlidingContent
        data={STEPS_DATA}
        selected={stepData?.name}
        listenToSelected
        autoHeight
      />
    </>
  );
}
